import { useState, useEffect } from 'react';
import { CheckCircle2, BookOpen, ChevronRight, Sparkles } from 'lucide-react';
import { QuickJournal } from '../components/QuickJournal';
import { ScorecardSummary } from '../components/ScorecardSummary';
import { TodayHero } from '../components/TodayHero';
import { CoachCTA } from '../components/CoachCTA';
import { ActivePlansWidget } from '../components/ActivePlansWidget';
import { supabase } from '../services/supabase';
import { useSubscription } from '../hooks/useSubscription';
import { useNavigate } from 'react-router-dom';
import { SimpleWelcome } from '../components/SimpleWelcome';
import { logger } from '../services/logger';

export function AppDashboard() {
  const navigate = useNavigate();
  const [showWelcome, setShowWelcome] = useState(false);
  const [userName, setUserName] = useState('');
  const [userProgress, setUserProgress] = useState({
    hasJournalEntry: false,
    hasPlan: false,
    hasCoached: false
  });
  const [dailyActivity, setDailyActivity] = useState({
    journalCompleted: false,
    planLocked: false,
    coachEngaged: false,
    actionTaken: false
  });
  const [todayJournalCount, setTodayJournalCount] = useState(0);
  const [hasActivePlans, setHasActivePlans] = useState(false);
  const [streak, setStreak] = useState(0);
  const [mounted, setMounted] = useState(false);
  const { status, trialEndsAt } = useSubscription();

  const trialDaysRemaining = trialEndsAt && status === 'trialing'
    ? Math.ceil((new Date(trialEndsAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : null;

  useEffect(() => {
    checkFirstTimeUser();
    setMounted(true);
  }, []);

  async function checkFirstTimeUser() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('welcome_completed, weekly_plan_locked_at, display_name')
        .eq('id', user.id)
        .maybeSingle();

      if (!profile?.welcome_completed) {
        setShowWelcome(true);
        return;
      }

      setUserName(profile?.display_name || '');

      const { count: journalCount } = await supabase
        .from('journal_entries')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      const { count: coachCount } = await supabase
        .from('coaching_conversations')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      const { count: planCount } = await supabase
        .from('user_action_plans')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .in('status', ['in_progress', 'not_started']);

      const hasJournalEntry = (journalCount || 0) > 0;
      const hasPlan = !!profile?.weekly_plan_locked_at;
      const hasCoached = (coachCount || 0) > 0;

      setUserProgress({ hasJournalEntry, hasPlan, hasCoached });
      setHasActivePlans((planCount || 0) > 0);

      const today = new Date().toISOString().split('T')[0];

      const { count: todayJournalCount } = await supabase
        .from('journal_entries')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('date', today);

      setTodayJournalCount(todayJournalCount || 0);

      const { data: dailyActivityData } = await supabase
        .from('daily_activities')
        .select('*')
        .eq('user_id', user.id)
        .eq('activity_date', today)
        .maybeSingle();

      if (dailyActivityData) {
        setDailyActivity({
          journalCompleted: dailyActivityData.journal_completed,
          planLocked: dailyActivityData.plan_locked,
          coachEngaged: dailyActivityData.coach_engaged,
          actionTaken: dailyActivityData.action_taken
        });
      }

      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const { count: recentActivityCount } = await supabase
        .from('daily_activities')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .gte('activity_date', sevenDaysAgo.toISOString().split('T')[0]);

      setStreak(recentActivityCount || 0);
    } catch (err) {
      logger.error('Error checking user status:', err);
    }
  }

  const handleEntryCreated = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const today = new Date().toISOString().split('T')[0];
        const { error: activityError } = await supabase.rpc('update_daily_activity', {
          p_user_id: user.id,
          p_activity_date: today,
          p_journal_completed: true
        });
        if (activityError) throw activityError;
      }
    } catch (err) {
      logger.error('Error updating daily activity:', err);
    }
    checkFirstTimeUser();
  };

  if (showWelcome) {
    return <SimpleWelcome />;
  }

  const showJournalFirst = todayJournalCount === 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {status === 'trialing' && trialDaysRemaining !== null && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100/50 px-4 py-3">
          <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <p className="text-sm font-medium text-amber-800">
                {trialDaysRemaining > 0
                  ? `${trialDaysRemaining} day${trialDaysRemaining !== 1 ? 's' : ''} left in your trial — your progress is already saved.`
                  : 'Your free trial has ended.'}
              </p>
            </div>
            <button
              onClick={() => navigate('/pricing')}
              className="text-xs font-semibold text-amber-700 border border-amber-300 bg-white px-3 py-1.5 rounded-full hover:bg-amber-50 transition-colors flex-shrink-0"
            >
              Upgrade — $9/mo
            </button>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto px-4 py-6">
        <TodayHero
          userName={userName}
          streak={streak}
          journalCompleted={dailyActivity.journalCompleted}
          planLocked={dailyActivity.planLocked}
          coachEngaged={dailyActivity.coachEngaged}
          actionTaken={dailyActivity.actionTaken}
        />

        <div className="mt-6 space-y-4">
          {showJournalFirst && (
            <div className={`transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '100ms' }}>
              <QuickJournal onSave={handleEntryCreated} />
            </div>
          )}

          {!showJournalFirst && todayJournalCount > 0 && (
            <div className={`bg-white rounded-2xl shadow-sm border border-gray-200/80 overflow-hidden transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '100ms' }}>
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex items-center justify-center">
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Journal Complete</h3>
                      <p className="text-sm text-gray-500">{todayJournalCount} {todayJournalCount === 1 ? 'entry' : 'entries'} captured today</p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/journal')}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-all group"
                  >
                    <BookOpen className="h-4 w-4" />
                    <span>View all</span>
                    <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className={`transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '200ms' }}>
            <CoachCTA
              hasEngagedToday={dailyActivity.coachEngaged}
              userName={userName}
            />
          </div>

          {hasActivePlans && (
            <div className={`transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '300ms' }}>
              <ActivePlansWidget />
            </div>
          )}

          {(userProgress.hasJournalEntry || userProgress.hasCoached) && (
            <div className={`transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '400ms' }}>
              <ScorecardSummary />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
